<template>
  <v-card variant="flat" class="ncrm-record-header pa-4">
    <div class="d-flex flex-wrap align-center justify-space-between ga-4">
      <div class="text-left">
        <div class="d-flex align-center ga-2">
          <v-btn
            v-if="showBackButton"
            size="small"
            variant="text"
            color="primary"
            icon="mdi-arrow-left"
            class="ncrm-record-header__back-btn"
            :aria-label="resolvedBackAriaLabel"
            @click="handleBackClick"
          />
          <h2 class="text-h5 ncrm-record-header__title">{{ title }}</h2>
        </div>
        <div v-if="subtitle" class="ncrm-record-header__subtitle text-body-2 text-medium-emphasis">
          {{ subtitle }}
        </div>
        <div v-if="normalizedChips.length" class="d-flex flex-wrap ga-2 mt-3">
          <v-tooltip v-for="(chip, index) in normalizedChips" :key="`ncrm-record-chip-${index}`" location="top">
            <template #activator="{ props: tooltipProps }">
              <v-chip
                v-bind="tooltipProps"
                class="ncrm-record-header__chip"
                size="small"
                :color="chip.color || 'primary'"
                :prepend-icon="chip.icon || undefined"
                :variant="chip.variant || 'tonal'"
              >
                {{ chip.text }}
              </v-chip>
            </template>
            <span>{{ chip.text }}</span>
          </v-tooltip>
        </div>
        <div v-if="normalizedKpis.length || $slots.kpis" class="ncrm-record-header__kpis mt-3">
          <slot name="kpis" :kpis="normalizedKpis" :is-kpi-clickable="isKpiClickable" :on-kpi-click="handleKpiClick">
            <component
              v-for="kpi in normalizedKpis"
              :key="`ncrm-record-kpi-${kpi.key}`"
              :is="isKpiClickable(kpi) ? 'button' : 'div'"
              class="ncrm-record-header__kpi"
              :class="{ 'ncrm-record-header__kpi--clickable': isKpiClickable(kpi) }"
              :type="isKpiClickable(kpi) ? 'button' : undefined"
              :aria-label="isKpiClickable(kpi) ? `${kpi.label}: ${kpi.value || '—'}` : undefined"
              @click="handleKpiClick(kpi)"
            >
              <div class="d-flex align-center ga-1 text-caption text-medium-emphasis">
                <v-icon v-if="kpi.icon" size="14">{{ kpi.icon }}</v-icon>
                <span>{{ kpi.label }}</span>
              </div>
              <div class="text-body-2 ncrm-record-header__kpi-value">
                {{ kpi.value || "—" }}
              </div>
            </component>
          </slot>
        </div>
      </div>

      <div v-if="$slots.actions" class="ncrm-record-header__actions d-flex align-center flex-wrap ga-2">
        <slot name="actions" />
      </div>
    </div>

    <div v-if="$slots.below" class="ncrm-record-header__below mt-3">
      <slot name="below" />
    </div>
  </v-card>
</template>

<script>
import { normalizeNcrmHeaderChips, normalizeNcrmHeaderKpis } from "@/Utile/ncrmRecordShell.mjs";

export default {
  name: "NcrmRecordHeader",
  emits: ["kpi-click", "back-click"],
  props: {
    label: {
      type: String,
      default: "CRM",
    },
    title: {
      type: String,
      required: true,
    },
    subtitle: {
      type: String,
      default: "",
    },
    chips: {
      type: Array,
      default: () => [],
    },
    kpis: {
      type: Array,
      default: () => [],
    },
    clickableKpiKeys: {
      type: Array,
      default: () => [],
    },
    showBackButton: {
      type: Boolean,
      default: false,
    },
    backAriaLabel: {
      type: String,
      default: "",
    },
  },
  computed: {
    normalizedChips() {
      return normalizeNcrmHeaderChips(this.chips);
    },
    normalizedKpis() {
      return normalizeNcrmHeaderKpis(this.kpis);
    },
    clickableKpiKeySet() {
      return new Set(
        (this.clickableKpiKeys || [])
          .map((key) => String(key || "").trim())
          .filter(Boolean)
      );
    },
    resolvedBackAriaLabel() {
      const explicit = String(this.backAriaLabel || "").trim();
      if (explicit) return explicit;
      const translated = this.$t?.("app.actions.back");
      if (typeof translated === "string" && translated !== "app.actions.back") return translated;
      return "Back";
    },
  },
  methods: {
    handleBackClick() {
      this.$emit("back-click");
    },
    isKpiClickable(kpi) {
      const key = String(kpi?.key || "").trim();
      return Boolean(key) && this.clickableKpiKeySet.has(key);
    },
    handleKpiClick(kpi) {
      if (!this.isKpiClickable(kpi)) return;
      this.$emit("kpi-click", kpi);
    },
  },
};
</script>

<style scoped>
.ncrm-record-header__title {
  color: var(--ncrm-color-text-strong);
  margin: 0;
}

.ncrm-record-header__back-btn {
  flex: 0 0 auto;
}

.ncrm-record-header__kpis {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ncrm-record-header__kpi {
  appearance: none;
  border: 1px solid var(--ncrm-color-border-strong);
  border-radius: var(--app-radius-base);
  padding: 8px 10px;
  min-width: 132px;
  background-color: var(--ncrm-color-surface-muted);
  color: inherit;
  font: inherit;
  text-align: left;
}

.ncrm-record-header__kpi--clickable {
  cursor: pointer;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.ncrm-record-header__kpi--clickable:hover {
  background-color: var(--ncrm-color-hover-default);
  border-color: var(--ncrm-color-primary-border);
}

.ncrm-record-header__kpi--clickable:focus-visible {
  outline: 2px solid var(--ncrm-color-primary-focus);
  outline-offset: 2px;
}

.ncrm-record-header__kpi-value {
  color: var(--ncrm-color-text-strong);
  font-weight: 600;
}

.ncrm-record-header__below {
  width: 100%;
}
</style>
